import React from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import type {
  ColDef,
  GetRowIdParams,
  SideBarDef,
} from '@ag-grid-community/core'
import '@ag-grid-community/styles/ag-theme-quartz.css'
import { DrawerGridPane } from './InvoiceGenerationView.styles'

// Two-line Plan Cost cells (invoice over BER) need more room than the theme default.
const ROW_HEIGHT = 44

/**
 * Excel-style column behaviour for every invoice-detail grid: click a header to
 * sort, open the header menu for a checkbox value filter. Columns opt out of
 * filtering individually (money columns pass `filter: false`).
 */
const DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  resizable: true,
  filter: 'agSetColumnFilter',
  menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
  minWidth: 90,
}

// Columns tool panel: show/hide any column, reorder by dragging.
const SIDE_BAR: SideBarDef = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      },
    },
  ],
}

interface InvoiceDataGridProps<TRow extends { lineItemId: string }> {
  rows: TRow[]
  columnDefs: ColDef<TRow>[]
  /** Accessible name for the grid, e.g. "Health Plans line items". */
  label: string
}

/**
 * Shared grid for the invoice-detail line-item tabs (Health Plans, Adjustments,
 * Medicare, YTD). Wraps ag-grid so every tab gets the same sorting, filtering, and column
 * show/hide behaviour without repeating the configuration per tab.
 */
const InvoiceDataGrid = <TRow extends { lineItemId: string }>({
  rows,
  columnDefs,
  label,
}: InvoiceDataGridProps<TRow>): React.ReactElement => (
  <DrawerGridPane className="ag-theme-quartz" role="group" aria-label={label}>
    <AgGridReact<TRow>
      rowData={rows}
      columnDefs={columnDefs}
      defaultColDef={DEFAULT_COL_DEF}
      sideBar={SIDE_BAR}
      getRowId={({ data }: GetRowIdParams<TRow>) => data.lineItemId}
      rowHeight={ROW_HEIGHT}
      tooltipShowDelay={300}
      suppressDragLeaveHidesColumns
      suppressCellFocus
    />
  </DrawerGridPane>
)

export { InvoiceDataGrid }
